import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Stack,
  Card,
  Popover,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import PersonIcon from "@mui/icons-material/Person";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { getUsers } from "../../services/userService";
import { updateMeeting, getMeetingById } from "../../services/meetingService";
import { User } from "../../types";
import Notification from "../Common/Notification";
import ConfirmDialog from "../Common/ConfirmDialog";
import useAcl from "../../hooks/useAcl";

interface Props {
  parentId: string;
  usersIds: string[];
  usersNames: Record<string, string>;
  usersColumns?: Record<string, { status: string }>;
  onRefresh: () => Promise<void>;
  onUpdateForm?: (data: {
    usersIds: string[];
    usersNames: Record<string, string>;
    usersColumns: Record<string, { status: string }>;
  }) => void;
  createdById: string;
}

const ParticipantsAccordion: React.FC<Props> = ({
  parentId,
  usersIds,
  usersNames,
  usersColumns = {},
  onRefresh,
  onUpdateForm,
  createdById,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(usersIds);
  const [users, setUsers] = useState<User[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "error" | "success";
  }>({ open: false, message: "", severity: "error" });

  const { canEdit } = useAcl("Meeting", { createdById });

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersList = await getUsers();
        setUsers(usersList);
      } catch (error) {
        console.error("Error loading users:", error);
        setNotification({
          open: true,
          message: t("meetingDetail.loadUsersError"),
          severity: "error",
        });
      }
    };
    loadUsers();
  }, [t]);

  useEffect(() => {
    setSelectedUserIds(usersIds);
  }, [usersIds]);

  const handleCheckboxChange = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddParticipants = async () => {
    if (!canEdit) {
      setNotification({
        open: true,
        message: t("meetingDetail.noEditPermission"),
        severity: "error",
      });
      return;
    }

    if (selectedUserIds.length === 0) {
      setNotification({
        open: true,
        message: t("meetingDetail.invalidParticipantSelection"),
        severity: "error",
      });
      return;
    }

    const updatedUsersIds = Array.from(new Set(selectedUserIds));
    const updatedUsersNames = { ...usersNames };
    const updatedUsersColumns = { ...usersColumns };

    selectedUserIds.forEach((userId) => {
      const user = users.find((u) => u.id === userId);
      if (user && !updatedUsersNames[userId]) {
        updatedUsersNames[userId] = user.name;
        updatedUsersColumns[userId] = { status: "Accepted" };
      }
    });

    if (parentId === "new" && onUpdateForm) {
      onUpdateForm({
        usersIds: updatedUsersIds,
        usersNames: updatedUsersNames,
        usersColumns: updatedUsersColumns,
      });
      setNotification({
        open: true,
        message: t("meetingDetail.addParticipantSuccess"),
        severity: "success",
      });
      setDialogOpen(false);
      return;
    }

    try {
      await updateMeeting(parentId, {
        usersIds: updatedUsersIds,
        usersNames: updatedUsersNames,
        usersColumns: updatedUsersColumns,
      });
      setDialogOpen(false);
      setNotification({
        open: true,
        message: t("meetingDetail.addParticipantSuccess"),
        severity: "success",
      });
      await onRefresh();
    } catch (error: any) {
      console.error("Error adding participants:", error);
      const message =
        error.response?.data?.messageTranslation?.label === "validationFailure"
          ? t("meetingDetail.validationError", {
              field:
                error.response?.data?.messageTranslation?.data?.field ||
                "unknown",
            })
          : t("meetingDetail.addParticipantError");
      setNotification({ open: true, message, severity: "error" });
    }
  };

  const handleConfirmDelete = async () => {
    if (!canEdit) {
      setNotification({
        open: true,
        message: t("meetingDetail.noEditPermission"),
        severity: "error",
      });
      setConfirmDeleteOpen(false);
      return;
    }

    if (!userToDelete) {
      console.error("No user selected for deletion");
      setNotification({
        open: true,
        message: t("meetingDetail.invalidParticipant"),
        severity: "error",
      });
      setConfirmDeleteOpen(false);
      return;
    }

    setIsLoading(true);

    if (!parentId || parentId === "new") {
      if (onUpdateForm) {
        const newUsersIds = usersIds.filter((id) => id !== userToDelete);
        const newUsersNames = { ...usersNames };
        const newUsersColumns = { ...usersColumns };
        delete newUsersNames[userToDelete];
        delete newUsersColumns[userToDelete];

        onUpdateForm({
          usersIds: newUsersIds,
          usersNames: newUsersNames,
          usersColumns: newUsersColumns,
        });
        setNotification({
          open: true,
          message: t("meetingDetail.removeParticipantSuccess"),
          severity: "success",
        });
      }
      setConfirmDeleteOpen(false);
      setAnchorEl(null);
      setUserToDelete(null);
      setIsLoading(false);
      return;
    }

    try {
      const meeting = await getMeetingById(parentId);
      const newUsersIds = (meeting.usersIds || []).filter(
        (id) => id !== userToDelete
      );
      const newUsersNames = { ...meeting.usersNames };
      const newUsersColumns = { ...meeting.usersColumns };
      delete newUsersNames[userToDelete];
      delete newUsersColumns[userToDelete];

      await updateMeeting(parentId, {
        usersIds: newUsersIds,
        usersNames: newUsersNames,
        usersColumns: newUsersColumns,
      });
      setNotification({
        open: true,
        message: t("meetingDetail.removeParticipantSuccess"),
        severity: "success",
      });
      await onRefresh();
    } catch (error: any) {
      console.error("Error removing participant:", error);
      console.error("Error details:", error.response?.data);
      const message =
        error.response?.data?.messageTranslation?.label === "validationFailure"
          ? t("meetingDetail.validationError", {
              field:
                error.response?.data?.messageTranslation?.data?.field ||
                "unknown",
            })
          : error.response?.data?.messageTranslation?.label ===
            "cannotRelateNonExisting"
          ? t("meetingDetail.nonExistingUser", { userId: userToDelete })
          : t("meetingDetail.removeParticipantError");
      setNotification({ open: true, message, severity: "error" });
    } finally {
      setConfirmDeleteOpen(false);
      setAnchorEl(null);
      setUserToDelete(null);
      setIsLoading(false);
    }
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    userId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setUserToDelete(userId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenConfirmDelete = () => {
    if (!userToDelete) {
      console.error("No user selected for deletion");
      setNotification({
        open: true,
        message: t("meetingDetail.invalidParticipant"),
        severity: "error",
      });
      return;
    }
    if (!canEdit) {
      setNotification({
        open: true,
        message: t("meetingDetail.noEditPermission"),
        severity: "error",
      });
      return;
    }
    setConfirmDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setAnchorEl(null);
    setUserToDelete(null);
  };

  const handleCloseNotification = () => {
    setNotification({ open: false, message: "", severity: "error" });
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        background: "#fff",
        mb: 2,
        minWidth: 280,
        maxWidth: 500,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          justifyContent: "space-between",
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="h4"
            sx={{
              flex: 1,
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          >
            {t("meetingDetail.participantsTitle")}
          </Typography>
          <IconButton
            size="small"
            sx={{
              transition: "transform 0.2s",
              mr: 1,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              color: "grey.700",
            }}
            disableRipple
            tabIndex={-1}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
        {canEdit && (
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              setDialogOpen(true);
            }}
            sx={{
              border: "1px solid #4467A5",
              borderRadius: "8px",
              bgcolor: "#4467A5",
              color: "#fff",
              "&:hover": { bgcolor: "#385894" },
            }}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          {usersIds.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ fontSize: "0.95rem", py: 2, textAlign: "center" }}
            >
              {t("meetingDetail.noParticipants")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {usersIds.map((userId) => (
                <Card
                  key={userId}
                  sx={{
                    width: "100%",
                    mb: 1,
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                    border: "none",
                    px: 2,
                    py: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", flex: 1 }}
                    >
                      <PersonIcon sx={{ color: "#4467A5", mr: 1 }} />
                      <Typography
                        sx={{ fontSize: { xs: "0.92rem", sm: "1rem" } }}
                      >
                        {usersNames[userId] || t("meetingDetail.unknownUser")}
                      </Typography>
                    </Box>
                    {canEdit && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, userId)}
                      >
                        <MoreHorizOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>

      {canEdit && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleOpenConfirmDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: "#D32F2F" }} />
            </ListItemIcon>
            <ListItemText
              primary={t("meetingDetail.participants.deleteButton")}
            />
          </MenuItem>
        </Popover>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        title={t("meetingDetail.deleteParticipantConfirmTitle")}
        content={t("meetingDetail.deleteParticipantConfirmContent")}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={isLoading}
      />

      {canEdit && (
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{t("meetingDetail.addParticipant")}</DialogTitle>
          <DialogContent>
            <List>
              {users.map((user) => (
                <ListItem key={user.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => handleCheckboxChange(user.id)}
                      />
                    }
                    label={user.name}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>
              {t("meetingDetail.cancelButton")}
            </Button>
            <Button variant="contained" onClick={handleAddParticipants}>
              {t("meetingDetail.addButton")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </Box>
  );
};

export default ParticipantsAccordion;
